import { useMemo, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";

import {
  ListBulletIcon,
  CheckIcon,
  BookmarkSquareIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import Header from "common/components/Header/Header";
import useList from "common/hooks/useList";
import FilteredList from "common/components/Table/FilteredList";
import RadioBlock from "common/components/RadioBlock/RadioBlock";
import TabBlockItem from "common/components/TabBlockItem/TabBlockItem";

import AlertsContext from "common/providers/alerts";
import UserSearch from "components/search/UserSearch";
import { Profile } from "types/Profile";
import CompanyPersonSelect from "components/select/CompanyPersonSelect/CompanyPersonSelect";
import CategorySelect from "components/select/CategorySelect/CategorySelect";
import WorkSiteIcon from "components/Icon/icons/WorkSiteIcon";
import SubscriptionItem from "./SubscriptionItem";

function Subscriptions() {
  const { t } = useTranslation();
  const { setAlert } = useContext(AlertsContext);

  const {
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    itemsCount,
    itemsPerPage,
    error: errorList1,
  } = useList<Profile>("profiles", {
    defaultSorts: ["updatedAt:DESC", "createdAt:DESC"],
    defaultFilters: {
      "subscriptionDate][$notNull": true,
    },
    populate: ["avatar", "companyLogo", "user", "user.role"],
  });

  const { items: profiles, fetchItems: fetchProfiles } = useList<Profile>(
    "profiles",
    {
      defaultFilters: {
        "subscriptionDate][$notNull": true,
      },
      fields: ["id", "isSubscribed", "premiumValidUntilDate"],
      manual: true,
    }
  );

  useEffect(() => {
    if (errorList1) {
      setAlert(errorList1, "danger");
    }
  }, [errorList1, setAlert]);

  const statusList = useMemo(() => {
    return [
      {
        id: "",
        name: t("status.all"),
        count: profiles?.length || 0,
        icon: ListBulletIcon,
        color: "primary",
      },
      {
        id: true,
        name: t("status.subscribed"),
        count: profiles?.filter((p: Profile) => p.isSubscribed)?.length || 0,
        icon: CheckIcon,
        color: "green",
      },
      {
        id: false,
        name: t("status.unsubscribed"),
        count: profiles?.filter((p: Profile) => !p.isSubscribed)?.length || 0,
        icon: XMarkIcon,
        color: "red",
      },
    ];
  }, [t, profiles]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const columns = useMemo(
    () => [
      {
        Header: t("forms.associatedProfile"),
        filterName: "username$containsi",
        filterLabel: t("filters.search"),
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => <UserSearch isFilter {...props} />,
      },
      {
        Header: t("forms.type"),
        filterName: "isCompany",
        filterComponent: (props: any) => (
          <CompanyPersonSelect isFilter {...props} />
        ),
        columnClasses: "md:w-1/3 xl:flex-1 xl:w-auto",
      },
      {
        Header: t("forms.category"),
        filterName: "services][id",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <CategorySelect isFilter Icon={WorkSiteIcon} {...props} />
        ),
      },
      /* {
        Header: t("forms.onlyPro"),
        filterName: "onlyPro",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <ButtonGroup
            className="w-full"
            options={[
              { value: null, label: t("status.all") },
              { value: "true", label: t("forms.onlyPro") },
            ]}
            {...props}
          />
        ),
      }, */
    ],
    [t]
  );

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500">
      <Header
        title={t("titlePages.subscriptions")}
        subTitle={t("titleLegends.subscriptions")}
        Icon={BookmarkSquareIcon}
        isBeforeFilters
        filters={filters}
        setFilter={setFilter}
        filterName="isSubscribed"
        FilterComponent={(props: any) => (
          <RadioBlock
            Component={TabBlockItem}
            items={statusList}
            horizontal
            noGap
            {...props}
          />
        )}
      />

      <FilteredList
        data={items ?? []}
        columns={columns} // used as filter items
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={SubscriptionItem}
        onDelete={null}
        onNavigate={null}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
    </div>
  );
}

export default Subscriptions;
